import { findTopLevelNavigationItemByPath } from "@/common/helpers/route";
import { useRouteConfig, useSidebarNavigation } from "@/common/hooks/use-navigation";
import { paletteCommandsStore } from "@/store/palette-commands-store";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { collectModuleCommands, collectNavigationCommands } from "../_helpers/collect-navigation-commands";
import type { PaletteCommandGroup } from "../types";
import { useGeneralCommands } from "./use-general-commands";
import { useRecentVisitCommands } from "./use-recent-visit-commands";
import { useUserSearchCommands } from "./use-user-search-commands";

export function useCommandGroups(search: string): PaletteCommandGroup[] {
  const { t } = useTranslation();
  const [location] = useLocation();
  const { routeConfig } = useRouteConfig();
  const { items: sidebarItems } = useSidebarNavigation();
  const pageCommandRegistrations = paletteCommandsStore((state) => state.registrations);
  const recentVisitCommands = useRecentVisitCommands();
  const generalCommands = useGeneralCommands();
  const { commands: userCommands, isLoading: isUserSearchLoading } = useUserSearchCommands(search);

  const registeredPageCommands = useMemo(() => {
    const commandsById = new Map(
      Object.values(pageCommandRegistrations)
        .flat()
        .map((command) => [command.id, command]),
    );
    return [...commandsById.values()];
  }, [pageCommandRegistrations]);
  const navigationCommands = useMemo(() => collectNavigationCommands(sidebarItems), [sidebarItems]);
  const moduleItem = useMemo(() => findTopLevelNavigationItemByPath(routeConfig, location), [routeConfig, location]);
  const { sectionItem, sectionCommands, moduleCommands } = useMemo(
    () => collectModuleCommands(moduleItem, location),
    [moduleItem, location],
  );
  // Configured commands have curated titles, so a page button leading to the same place is not listed twice.
  const pageCommands = useMemo(() => {
    const configuredTargets = new Set(
      [...sectionCommands, ...moduleCommands, ...navigationCommands].flatMap((command) =>
        "to" in command ? [command.to] : [],
      ),
    );
    return registeredPageCommands.filter((command) => !("to" in command) || !configuredTargets.has(command.to));
  }, [registeredPageCommands, sectionCommands, moduleCommands, navigationCommands]);

  return useMemo(() => {
    const groups: PaletteCommandGroup[] = [
      { id: "page", heading: t("CommandPaletteThisPage"), commands: pageCommands },
      {
        id: "section-actions",
        heading: t("CommandPaletteModuleActions", { module: sectionItem?.title() }),
        commands: sectionCommands,
      },
      {
        id: "module-actions",
        heading: t("CommandPaletteModuleActions", { module: moduleItem?.title() }),
        commands: moduleCommands,
      },
      { id: "recent", heading: t("CommandPaletteRecentlyVisited"), commands: recentVisitCommands },
      {
        id: "people",
        heading: t("CommandPalettePeople"),
        commands: userCommands,
        isPreFiltered: true,
        isLoading: isUserSearchLoading,
      },
      { id: "navigation", heading: t("CommandPaletteGoTo"), commands: navigationCommands },
      { id: "actions", heading: t("Actions"), commands: generalCommands },
    ];

    return groups.filter((group) => group.commands.length > 0 || group.isLoading);
  }, [
    t,
    pageCommands,
    sectionItem,
    sectionCommands,
    moduleItem,
    moduleCommands,
    recentVisitCommands,
    userCommands,
    isUserSearchLoading,
    navigationCommands,
    generalCommands,
  ]);
}
